import type { CrossroadsLog } from '@/api/hooks/useIntegrationLogs'
import type {
  OrderGridRow,
  OrderPerspectiveView,
  ProductRatio,
} from '@/api/hooks/useOrders'
import { formatDeliveryWindow } from '@/api/hooks/useOrders'
import { ProductPill } from '@/components/Display/ProductPill'
import { UpdatedByTag } from '@/components/Display/UpdatedByTag'
import { VerticalDots } from '@/components/Display/VerticalDots'
import {
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  FileAddOutlined,
  LinkOutlined,
  NodeIndexOutlined,
  StopOutlined,
  SwapOutlined,
  SyncOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { BBDTag, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Button, Dropdown, Popover, Tag } from 'antd'
import type React from 'react'
import { useNavigate } from 'react-router-dom'

// Status color mapping for IntegrationStatus
export const integrationStatusConfig: Record<
  string,
  {
    color: string
    label: string
    icon: React.ReactNode
    isError?: boolean
    isChangeRequest?: boolean
    isOrderRequest?: boolean
  }
> = {
  synced: { color: 'green', label: 'Synced', icon: <CheckCircleOutlined /> },
  origin_mapped: {
    color: 'orange',
    label: 'Origin Mapped',
    icon: <SwapOutlined />,
  },
  master_mapped: {
    color: 'blue',
    label: 'Master Mapped',
    icon: <LinkOutlined />,
  },
  destination_mapped: {
    color: 'purple',
    label: 'Dest Mapped',
    icon: <NodeIndexOutlined />,
  },
  origin_mapping_error: {
    color: 'red',
    label: 'Mapping Error',
    icon: <ExclamationCircleOutlined />,
    isError: true,
  },
  error: {
    color: 'red',
    label: 'Error',
    icon: <CloseCircleOutlined />,
    isError: true,
  },
  rejected: {
    color: 'default',
    label: 'Rejected',
    icon: <StopOutlined />,
    isError: true,
  },
  canceled: {
    color: 'default',
    label: 'Canceled',
    icon: <StopOutlined />,
  },
  change_requested: {
    color: 'purple',
    label: 'Change Requested',
    icon: <ClockCircleOutlined />,
    isChangeRequest: true,
  },
  requested: {
    color: 'blue',
    label: 'Order Requested',
    icon: <FileAddOutlined />,
    isOrderRequest: true,
  },
}

// Color, label, and icon mapping for trip_status (OrderProgressStatus)
export const tripStatusConfig: Record<
  string,
  { color: string; label: string; icon: React.ReactNode }
> = {
  no_progress: {
    color: 'default',
    label: 'No Progress',
    icon: <ClockCircleOutlined />,
  },
  assigned: { color: 'blue', label: 'Assigned', icon: <UserOutlined /> },
  driving_to_load: {
    color: 'blue',
    label: 'Driving to Load',
    icon: <CarOutlined />,
  },
  arrived_at_load: {
    color: 'purple',
    label: 'Arrived at Load',
    icon: <EnvironmentOutlined />,
  },
  loading: { color: 'purple', label: 'Loading', icon: <SyncOutlined /> },
  completed_load: {
    color: 'cyan',
    label: 'Completed Load',
    icon: <CheckCircleOutlined />,
  },
  driving_to_drop: {
    color: 'orange',
    label: 'Driving to Drop',
    icon: <CarOutlined />,
  },
  arrived_at_drop: {
    color: 'orange',
    label: 'Arrived at Drop',
    icon: <EnvironmentOutlined />,
  },
  dropping: { color: 'orange', label: 'Dropping', icon: <SyncOutlined /> },
  completed_drop: {
    color: 'lime',
    label: 'Completed Drop',
    icon: <CheckCircleOutlined />,
  },
  complete: {
    color: 'green',
    label: 'Complete',
    icon: <CheckCircleOutlined />,
  },
  canceled: {
    color: 'default',
    label: 'Canceled',
    icon: <CloseCircleOutlined />,
  },
}

interface OrderCellRendererParams {
  data?: OrderGridRow
  value?: unknown
  dateRange?: { start: string; end: string }
}

/** Opens the log details drawer. The group id rides along so the drawer can
 * fetch the surrounding group for context, but the log itself is the one
 * already on the row -- no round trip to find it. */
export type LogClickHandler = (
  log: CrossroadsLog,
  logGroupId: string | null,
) => void

interface IntegrationStatusRendererParams extends OrderCellRendererParams {
  onLogClick?: LogClickHandler
  onChangeRequestClick?: (changeId: string, order: OrderPerspectiveView) => void
}

interface ActionsRendererParams extends OrderCellRendererParams {
  onHistoryClick?: (order: OrderPerspectiveView) => void
}

/**
 * Renders the integration status as a colored tag.
 *
 * Clicking it opens the row's own log in the details drawer, whatever the
 * status -- an order that synced has a log worth reading too. A pending
 * change request takes precedence, since that has its own drawer to open.
 */
export function IntegrationStatusRenderer({
  data,
  onLogClick,
  onChangeRequestClick,
}: IntegrationStatusRendererParams) {
  if (!data?.status) return null

  const configKey =
    data.status === 'requested' && !data.pending_change_id
      ? 'order_requested'
      : data.status
  const config = integrationStatusConfig[configKey] || {
    color: 'default',
    label: data.status,
    icon: null,
  }

  const isChangeRequestClickable =
    config.isChangeRequest && data.pending_change_id && onChangeRequestClick
  const log = data.log

  let handleClick: (() => void) | undefined
  if (isChangeRequestClickable) {
    handleClick = () => onChangeRequestClick(data.pending_change_id!, data)
  } else if (log && onLogClick) {
    handleClick = () => onLogClick(log, data.log_group_id ?? null)
  }

  return (
    <div>
      <BBDTag
        color={config.color}
        onClick={handleClick}
        className={handleClick ? 'cursor-pointer' : undefined}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {config.icon} {config.label}
        </span>
      </BBDTag>
    </div>
  )
}

/**
 * Renders the trip status as a colored tag with icon
 */
export function TripStatusRenderer({ value }: { value: string | null }) {
  if (!value) return null

  const config = tripStatusConfig[value] || {
    color: 'default',
    label: value,
    icon: null,
  }

  return (
    <Tag color={config.color}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {config.icon} {config.label}
      </span>
    </Tag>
  )
}

/**
 * Renders the delivery window as date and time range
 */
export function DeliveryWindowRenderer({ data }: OrderCellRendererParams) {
  if (!data) return null

  const formatted = formatDeliveryWindow(
    data.delivery_window_start,
    data.delivery_window_end,
  )

  if (!formatted)
    return <span style={{ color: 'var(--text-tertiary)' }}>-</span>

  return <span>{formatted}</span>
}

/**
 * Renders product ratios as pills that wrap when needed
 */
export function ProductsRenderer({ data }: OrderCellRendererParams) {
  if (!data?.product_ratios || data.product_ratios.length === 0) {
    return <span style={{ color: 'var(--text-tertiary)' }}>-</span>
  }

  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '4px 0' }}
    >
      {data.product_ratios.map((product: ProductRatio, index) => (
        <ProductPill
          key={`${product.name}-${index}`}
          name={product.name}
          amount={product.amount}
          volume={product.volume}
        />
      ))}
    </div>
  )
}

interface UpdatedByRendererParams extends OrderCellRendererParams {
  onHistoryClick?: (order: OrderPerspectiveView) => void
}

/**
 * Renders the updated by information with a popover showing full history details
 */
export function UpdatedByRenderer({
  data,
  onHistoryClick,
}: UpdatedByRendererParams) {
  if (!data?.created_by || !data?.created_date) {
    return null
  }

  const handleHistoryClick = () => {
    onHistoryClick?.(data)
  }

  return (
    <div>
      <UpdatedByTag
        createdBy={data.created_by}
        createdDate={data.created_date}
        updatedBy={data.updated_by ?? undefined}
        updatedDate={data.updated_date ?? undefined}
        onClick={handleHistoryClick}
      />
    </div>
  )
}

interface IntegrationErrorRendererParams {
  log?: CrossroadsLog | null
  onClick?: () => void
}

/**
 * The row's log, but only when it's something gone wrong -- a healthy log
 * isn't an issue to report. Shared with the column's valueGetter so the
 * hidden Issue column filters on exactly what it would display.
 */
export function issueTitle(log: CrossroadsLog | null | undefined) {
  if (!log || (log.status !== 'failed' && log.status !== 'warning')) return null
  return log.title ?? null
}

/**
 * Renders integration error with a warning icon and popover showing details.
 * Clicking opens the log details drawer.
 */
export function IntegrationErrorRenderer({
  log,
  onClick,
}: IntegrationErrorRendererParams) {
  const title = issueTitle(log)
  const message = log?.detail?.message

  if (!title) return null

  const handleClick = () => onClick?.()

  const popoverContent = (
    <Vertical gap={8} style={{ maxWidth: 300 }}>
      <Texto weight='600'>{title}</Texto>
      {message && (
        <Texto appearance='hint' style={{ fontSize: 13 }}>
          {message}
        </Texto>
      )}
    </Vertical>
  )

  return (
    <Popover content={popoverContent} trigger='hover' placement='bottomLeft'>
      <span
        className='cursor-pointer'
        onClick={handleClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--theme-warning)',
        }}
      >
        <WarningOutlined />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
      </span>
    </Popover>
  )
}

/**
 * Renders actions dropdown menu
 */
export function ActionsRenderer({
  data,
  onHistoryClick,
}: ActionsRendererParams) {
  const navigate = useNavigate()

  if (!data) return null

  const handleViewLogs = () => {
    // The connection logs view filters on origin_order_number, which maps to
    // internal_order_number on every perspective.
    if (!data.internal_order_number) return
    navigate(
      `/integration-management/connection-logs?order=${encodeURIComponent(data.internal_order_number)}`,
    )
  }

  const handleViewHistory = () => {
    onHistoryClick?.(data)
  }

  const menuItems = [
    {
      key: 'logs',
      label: 'View Logs',
      onClick: handleViewLogs,
    },
    {
      key: 'history',
      label: 'Order History',
      onClick: handleViewHistory,
    },
  ]

  return (
    <Dropdown menu={{ items: menuItems }} trigger={['click']}>
      <Button type='text' icon={<VerticalDots />} />
    </Dropdown>
  )
}
